import React, { useState } from 'react';
import { Trash2, X } from 'lucide-react';
import * as Sentry from '@sentry/browser';
import styles from '../../styles/chat-history.module.css';
import { useTranslation } from 'react-i18next';
import { customToast } from 'components/toast';
import { useDeleteChatSessionMutation } from 'redux_store/services/borrower-profile';
import DeleteChatPopup from './DeleteChatPopup';

const ChatHistoryModal = ({
  isMobile = false,
  isChatOpen,
  isVectorClicks,
  setIsChatOpen,
  newChatAction,
  setChatWindowOpens,
  groupedChats,
  setVectorClicks,
  setNewChatOpens,
  setNewChatSelected,
  isNewChatPending = false,
  isB2C = false,
  activeChatId = null,
  onActiveChatDeleted,
  isSessionBusy = false,
}) => {
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleteChatSession, { isLoading: isDeleting }] =
    useDeleteChatSessionMutation();
  const { t } = useTranslation(['chat']);

  // The highlighted row always tracks the parent's active chat
  // (activeChatId), so the selection survives title updates, list
  // refetches, and auto-promotion of a freshly created session.
  const timeCategoryLabels = {
    Today: t('chat:history.timeCategories.today'),
    Yesterday: t('chat:history.timeCategories.yesterday'),
    Earlier: t('chat:history.timeCategories.earlier'),
  };
  const lockedTooltip = isSessionBusy
    ? t('chat:history.lockedTooltip')
    : undefined;

  const handleConfirmDelete = async () => {
    if (!pendingDelete?.chatId) return;
    const { chatId, title } = pendingDelete;
    try {
      await deleteChatSession({ chatId, isB2C }).unwrap();
      customToast.success('Chat deleted');
      if (chatId === activeChatId && typeof onActiveChatDeleted === 'function') {
        onActiveChatDeleted();
      }
    } catch (error) {
      // 404 means the chat was already gone — treat as success so the
      // sidebar still refreshes (the invalidate runs on success only,
      // so we re-trigger the same effect by leaving pendingDelete open
      // for the failure-toast branch). For other errors, surface them.
      const status = error?.status ?? error?.originalStatus;
      if (status === 404) {
        customToast.success('Chat deleted');
      } else {
        Sentry.captureException(error);
        customToast.error("Couldn't delete chat — try again");
      }
    } finally {
      setPendingDelete(null);
    }
  };

  return (
    <>
      {(isChatOpen || isVectorClicks) && (
        <div className={`${styles.historyModal} ${isMobile ? styles.historyModalMobile : ''}`}>
          <div className={styles.historyHeader}>
            <div className={styles.historyHeaderRow}>
              <div className={styles.historyHeaderLeft}>
                {isVectorClicks && (
                  <button
                    className={styles.historyVectorButton}
                    onClick={() => {
                      setVectorClicks(false);
                      setIsChatOpen(false);
                      setNewChatOpens(false);
                    }}
                    onFocus={(e) => e.target.blur()}
                    tabIndex={-1}
                  >
                    <img
                      src="/vector.png"
                      alt="VectorImg"
                      className={styles.historyVectorIcon}
                      draggable="false"
                    />
                  </button>
                )}
                <div className={styles.historyHeaderBrand}>
                  <img
                    src="/fundmoreai.png"
                    alt="fundmore.ai"
                    className={styles.historyHeaderLogo}
                  />
                  <div className={styles.historyHeaderWordmark}>
                    <span className={styles.historyHeaderFund}>fund</span>
                    <span className={styles.historyHeaderMore}>more</span>
                    <span className={styles.historyHeaderAi}>.ai</span>
                  </div>
                </div>
                <span className={styles.historyHeaderLabel}>{t('chat:history.title')}</span>
              </div>
              <div className={styles.historyHeaderRight}>
                <button
                  type="button"
                  className={styles.historyCloseButton}
                  onClick={() => {
                    setIsChatOpen(false);
                    setVectorClicks(false);
                    setNewChatOpens(false);
                  }}
                >
                  <X
                    className={styles.historyCloseIcon}
                  />
                </button>
              </div>
            </div>
          </div>
          <>
                <div className={styles.historyNewChat}>
                  <button
                    type="button"
                    onClick={() => {
                      setNewChatSelected(true);
                      newChatAction();
                    }}
                    className={`${styles.historyNewChatButton} ${
                      isSessionBusy ? styles.locked : ''
                    }`}
                    disabled={isSessionBusy}
                    aria-disabled={isSessionBusy || undefined}
                    title={lockedTooltip}
                  >
                    <span>{t('chat:history.newChat')}</span>
                  </button>
                </div>

            <div className={styles.historyList}>
              <div className={styles.historyScroll}>
                {isNewChatPending && (
                  <div className={styles.historySection}>
                    <div className={styles.historySectionTitle}>
                      {timeCategoryLabels.Today}
                    </div>
                    <div className={styles.historyItems}>
                      <div
                        className={`${styles.historyItem} ${styles.historyItemActive}`}
                        title="New Chat"
                      >
                        <span className={styles.historyItemText}>New Chat</span>
                      </div>
                    </div>
                  </div>
                )}
                {Object.entries(groupedChats)?.map(
                  ([timeCategory, chats]) =>
                    chats?.length > 0 && (
                      <div key={timeCategory} className={styles.historySection}>
                        <div className={styles.historySectionTitle}>
                          {timeCategoryLabels[timeCategory] ?? timeCategory}
                        </div>
                        <div className={styles.historyItems}>
                          {chats?.map((chat) => {
                            const activateChat = () => {
                              if (isSessionBusy) return;
                              setVectorClicks(false);
                              setIsChatOpen(false);
                              setNewChatOpens(false);
                              setChatWindowOpens(chat?.chat_id);
                            };
                            return (
                              <div
                                key={chat?.chat_id}
                                className={`group flex items-center justify-between ${styles.historyItem} ${
                                  activeChatId === chat?.chat_id
                                    ? styles.historyItemActive
                                    : ''
                                } ${isSessionBusy ? styles.locked : ''}`}
                                role="button"
                                tabIndex={isSessionBusy ? -1 : 0}
                                aria-disabled={isSessionBusy || undefined}
                                onClick={activateChat}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    activateChat();
                                  }
                                }}
                                title={
                                  isSessionBusy
                                    ? lockedTooltip
                                    : chat?.title ?? 'New Chat'
                                }
                              >
                                <span className={`${styles.historyItemText} flex-1 min-w-0 truncate`}>
                                  {chat?.title ?? 'New Chat'}
                                </span>
                                <button
                                  type="button"
                                  aria-label="Delete chat"
                                  className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-black/5 text-gray-500 hover:text-red-600"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setPendingDelete({
                                      chatId: chat?.chat_id,
                                      title: chat?.title,
                                    });
                                  }}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )
                )}
              </div>
            </div>
          </>
          <DeleteChatPopup
            isOpen={Boolean(pendingDelete)}
            chatTitle={pendingDelete?.title}
            isDeleting={isDeleting}
            onCancel={() => {
              if (!isDeleting) setPendingDelete(null);
            }}
            onConfirm={handleConfirmDelete}
          />
        </div>
      )}
    </>
  );
};
export default ChatHistoryModal;
